import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { clienteApi, money, formatDate, StatusPill, publicPdfUrl } from "@/lib/api";
import { useClienteAuth } from "@/lib/clienteAuth";
import { FileText, LogOut, Eye, Download, MessageCircle, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import InstallAppButton from "@/components/InstallAppButton";
import SolicitarOrcamento from "@/components/SolicitarOrcamento";

const FILTROS = [
  { key: "todos", label: "Todos", statuses: null },
  { key: "aguardando", label: "Aguardando", statuses: ["enviado", "visualizado", "expirado"] },
  { key: "aprovado", label: "Aprovados", statuses: ["aprovado"] },
  { key: "finalizado", label: "Finalizados", statuses: ["finalizado"] },
  { key: "recusado", label: "Reprovados", statuses: ["recusado"] },
];

export default function PortalCliente() {
  const { ctx, loading, logout } = useClienteAuth();
  const [orcamentos, setOrcamentos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [solicitarOpen, setSolicitarOpen] = useState(false);
  const [filtro, setFiltro] = useState("todos");
  const [busca, setBusca] = useState("");

  const carregarOrcamentos = () => {
    setCarregando(true);
    clienteApi.get("/portal/orcamentos")
      .then(({ data }) => setOrcamentos(data))
      .catch(() => toast.error("Erro ao carregar orçamentos"))
      .finally(() => setCarregando(false));
  };

  useEffect(() => {
    if (loading || !ctx) return;
    carregarOrcamentos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx, loading]);

  if (loading) {
    return <div className="min-h-screen bg-zinc-50 flex items-center justify-center text-sm text-zinc-500">Carregando…</div>;
  }
  if (!ctx) return <Navigate to="/portal/login" replace />;

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="portal-cliente">
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500">Portal do cliente</div>
            <div className="font-heading text-lg sm:text-xl font-bold">
              Olá, {ctx.cliente?.nome || "cliente"}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <InstallAppButton testid="portal-install-app-btn" />
            <button
              onClick={logout}
              data-testid="portal-logout"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold text-zinc-700 hover:bg-zinc-100"
            >
              <LogOut className="w-3.5 h-3.5" /> Sair
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="mb-4 flex items-baseline justify-between">
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Seus orçamentos</h1>
          <span className="text-xs text-zinc-500">{orcamentos.length} no total</span>
        </div>

        {orcamentos.length > 0 && (
          <PortalFiltros
            orcamentos={orcamentos}
            filtro={filtro}
            setFiltro={setFiltro}
            busca={busca}
            setBusca={setBusca}
          />
        )}

        {carregando ? (
          <div className="text-sm text-zinc-500">Carregando orçamentos…</div>
        ) : orcamentos.length === 0 ? (
          <div className="bbh-card p-8 text-center text-sm text-zinc-500">
            Você ainda não tem orçamentos por aqui. Assim que a BBH enviar um novo, ele aparecerá nesta lista.
          </div>
        ) : (
          <PortalOrcamentosList orcamentos={orcamentos} filtro={filtro} busca={busca} />
        )}

        {/* CTA fixo: solicitar novo orçamento */}
        <div className="mt-6">
          <button
            type="button"
            onClick={() => setSolicitarOpen(true)}
            data-testid="solicitar-orcamento-btn"
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md bbh-btn-primary text-sm font-semibold shadow-sm"
          >
            <Plus className="w-4 h-4" /> Solicitar orçamento
          </button>
          <p className="text-center text-xs text-zinc-500 mt-2">
            Descreva o que precisa e envie fotos. A BBH recebe seu pedido na hora.
          </p>
        </div>
      </main>

      <SolicitarOrcamento
        open={solicitarOpen}
        onOpenChange={setSolicitarOpen}
        onCreated={carregarOrcamentos}
      />
    </div>
  );
}


function PortalFiltros({ orcamentos, filtro, setFiltro, busca, setBusca }) {
  const contagem = useMemo(() => {
    const c = { todos: orcamentos.length };
    for (const f of FILTROS) {
      if (!f.statuses) continue;
      c[f.key] = orcamentos.filter((o) => f.statuses.includes(o.status)).length;
    }
    return c;
  }, [orcamentos]);

  return (
    <div className="mb-4 space-y-3">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
        <input
          type="text"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder="Buscar por número do orçamento…"
          data-testid="portal-busca"
          className="w-full pl-9 pr-3 py-2.5 rounded-md border border-zinc-200 bbh-input text-sm bg-white"
        />
      </div>
      <div className="flex flex-wrap gap-1.5" data-testid="portal-filtros">
        {FILTROS.map((f) => {
          const count = contagem[f.key] || 0;
          const ativo = filtro === f.key;
          return (
            <button
              key={f.key}
              type="button"
              onClick={() => setFiltro(f.key)}
              data-testid={`portal-filtro-${f.key}`}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                ativo
                  ? "bg-zinc-900 text-white border-zinc-900"
                  : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400"
              }`}
            >
              {f.label}
              <span
                className={`inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded-full text-[10px] font-bold ${
                  ativo ? "bg-white text-zinc-900" : "bg-zinc-100 text-zinc-600"
                }`}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function PortalOrcamentosList({ orcamentos, filtro, busca }) {
  const filtrados = useMemo(() => {
    const f = FILTROS.find((x) => x.key === filtro);
    const buscaNorm = busca.trim().toLowerCase();
    return orcamentos.filter((o) => {
      if (f?.statuses && !f.statuses.includes(o.status)) return false;
      if (buscaNorm) {
        const num = String(o.numero_orcamento || "").toLowerCase();
        if (!num.includes(buscaNorm)) return false;
      }
      return true;
    });
  }, [orcamentos, filtro, busca]);

  if (filtrados.length === 0) {
    return (
      <div className="bbh-card p-8 text-center text-sm text-zinc-500" data-testid="portal-lista-vazia">
        Nenhum orçamento nesse filtro.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {filtrados.map((o) => (
        <div key={o.id} className="bbh-card p-4" data-testid={`portal-orc-${o.numero_orcamento}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-zinc-500 shrink-0" />
                <div className="font-heading font-bold text-base">
                  Orçamento nº {o.numero_orcamento}
                </div>
              </div>
              <div className="text-xs text-zinc-500 mt-1">
                Criado em {formatDate(o.criado_em)}{" · "}
                {(o.itens || []).length} {(o.itens || []).length === 1 ? "item" : "itens"}
              </div>
            </div>
            <StatusPill status={o.status} pagamentoStatus={o.pagamento_status} />
          </div>
          <div className="mt-3 flex items-baseline justify-between">
            <span className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500">Valor total</span>
            <span className="font-heading text-lg font-bold" style={{ color: "var(--bbh-navy)" }}>{money(o.valor_total)}</span>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Link
              to={`/q/${o.token_publico}`}
              data-testid={`portal-ver-${o.numero_orcamento}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-zinc-900 text-white text-xs font-semibold"
            >
              <Eye className="w-3.5 h-3.5" /> Ver / Aprovar
            </Link>
            <a
              href={publicPdfUrl(o.token_publico, true)}
              data-testid={`portal-pdf-${o.numero_orcamento}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white border border-zinc-200 text-xs font-semibold"
            >
              <Download className="w-3.5 h-3.5" /> PDF
            </a>
          </div>
        </div>
      ))}
    </div>
  );
}
